import React, { useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef } from '@column/ui';

import { JSONButton } from '../../Events';
import { PopoverFilterEntry, Section } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { PaginatedDataTable, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { EventRepository, parseEvent } from '~/repositories';
import { Inner } from '~/styles';
import { EventEvent } from '~/typings/API';
import { formatString } from '~/util';

import { CardProps } from './Card';

interface Params {
  id: string;
}

// Card event types
const CARD_EVENT_TYPES = ['cards.card.created', 'cards.card.activated', 'cards.card.paused', 'cards.card.inactive'];

const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  margin: -4px 0;
  gap: 4px;
`;

const EventType = styled.h4`
  flex: auto;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: 0;
  flex-grow: 0;
  padding: 0;
  text-transform: capitalize;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const getCardEventLabel = (eventType: string): string => {
  const parts = eventType.split('.');
  if (parts.length < 3) return formatString(eventType);

  const action = parts[2];
  switch (action) {
    case 'created':
      return 'Card Created';
    case 'activated':
      return 'Card Activated';
    case 'paused':
      return 'Card Paused';
    case 'inactive':
      return 'Card Deactivated';
    default:
      return formatString(action);
  }
};

export const PageCardEvents: React.FC = () => {
  const { id: cardId } = useParams<keyof Params>() as Params;
  useOutletContext<CardProps>(); // Ensures we're in the card context
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-events');

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    // Fetch events filtered by object_id (card ID) server-side
    return EventRepository.getAll({
      ...params,
      objectIds: [cardId],
    }).then((response) => {
      const entries = response.events.map((event: EventEvent) => ({
        ...parseEvent(event),
        action: (
          <Action>
            <JSONButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEventDetail(event.data);
              }}
            >
              JSON
            </JSONButton>
          </Action>
        ),
      }));

      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  };

  const filter: PopoverFilterEntry[] = [
    {
      id: 'types',
      label: 'Type',
      type: 'select',
      list: CARD_EVENT_TYPES.map((eventType) => ({
        label: getCardEventLabel(eventType),
        value: eventType,
      })),
    },
    {
      id: 'created',
      label: 'Created',
      type: 'date',
    },
  ];

  const columns: DataTableColumnDef<Record<string, any>>[] = [
    {
      header: 'Event',
      accessor: 'eventType',
      width: 'minmax(200px, auto)',
      cell: ({ value }) => (
        <EventMeta>
          <EventType>{getCardEventLabel(value)}</EventType>
          <EventDetail>
            <span>{value}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    getTimestampColumn<Record<string, any>>({
      accessor: 'createdAt',
      label: 'Date',
      timeFormat,
      onTimeFormatChange,
      width: 'minmax(140px, 180px)',
    }),
    {
      header: '',
      accessor: 'action',
      width: '80px',
      cell: ({ value }) => value,
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <Inner p={0}>
        <Section headline="Events">
          <PaginatedDataTable<Record<string, any>>
            tableId="card-events"
            fetch={handleFetch}
            filter={filter}
            empty={{
              icon: <Icon.AnimationSignal />,
              title: 'No events yet',
              description: 'Events will appear here when card status changes occur.',
            }}
            columns={columns}
            rowClassName="min-h-11 py-1"
          />
        </Section>
      </Inner>
    </>
  );
};
